import React from 'react';
import useSWR from 'swr';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Grid, Typography } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import { getApiBaseUrl, fetcherRilis } from './PenggunaAplikasi';

const mb = (b) => (b ? `${(b / 1024 / 1024).toFixed(0)} MB` : '');
const baris = (t) => String(t || '').split('\n').map((s) => s.replace(/^\s*[-•*]\s*/, '').trim()).filter(Boolean);

/** Admin & superadmin: unduh aplikasi Juragan POS untuk dipasang pertama kali di HP. */
export const DownloadAplikasi = () => {
  const base = getApiBaseUrl();
  const { data, error, isLoading } = useSWR(`${base}/aplikasi/download`, fetcherRilis);
  const r = data?.data;
  const url64 = r?.unduh64 ? `${base}${r.unduh64}` : null;
  const url32 = r?.unduh32 ? `${base}${r.unduh32}` : null;
  // QR dibuka dari HP: alamat "localhost" tidak bisa diakses HP.
  const lokal = /^(localhost|127\.)/.test(new URL(base).hostname);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Typography variant="h5" fontWeight={700}>Download Aplikasi</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pasang aplikasi Juragan POS di HP kasir, karyawan, atau admin. Setelah terpasang, pembaruan berikutnya cukup dari dalam aplikasi.
      </Typography>
      {isLoading && <CircularProgress size={28} />}
      {error && <Alert severity="error">{error.response?.data?.message || error.message}</Alert>}
      {data && !r && <Alert severity="info">Belum ada rilis aplikasi. Superadmin mengunggahnya di menu Pengaturan › Rilis Aplikasi.</Alert>}
      {r && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box component="img" src="/kasir-icon.png" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    sx={{ width: 64, height: 64, borderRadius: 2 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={700}>POS Juragan Kuliner</Typography>
                    <Typography color="text.secondary">Versi {r.versi} · Android 6.0 ke atas</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
                  {url64 && (
                    <Button variant="contained" size="large" href={url64}>Download aplikasi {mb(r.ukuran64)}</Button>
                  )}
                  {url32 && <Button variant="outlined" href={url32}>Download untuk HP lama {mb(r.ukuran32)}</Button>}
                </Box>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2 }}>
                  Tautan berlaku 7 hari (muat ulang halaman ini untuk tautan baru).
                </Typography>
                {(baris(r.catatanTambahan).length > 0 || baris(r.catatanPerbaikan).length > 0) && (
                  <>
                    <Typography variant="subtitle2">Yang baru di versi {r.versi}</Typography>
                    <Box component="ul" sx={{ mt: 0.5, pl: 3, mb: 0 }}>
                      {baris(r.catatanTambahan).map((s) => <li key={`t${s}`}><Typography variant="body2">{s}</Typography></li>)}
                      {baris(r.catatanPerbaikan).map((s) => <li key={`p${s}`}><Typography variant="body2">Perbaikan: {s}</Typography></li>)}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Scan dari HP</Typography>
                {url64 && <QRCodeCanvas value={url64} size={200} includeMargin />}
                {lokal && (
                  <Alert severity="warning" sx={{ mt: 1, textAlign: 'left' }}>
                    Web admin dibuka lewat "localhost", jadi QR ini tidak bisa dibuka dari HP. Buka web admin lewat alamat IP / domain.
                  </Alert>
                )}
                <Box component="ol" sx={{ textAlign: 'left', pl: 3, mt: 1, mb: 0 }}>
                  <li><Typography variant="body2">Scan QR dengan kamera HP, lalu unduh.</Typography></li>
                  <li><Typography variant="body2">Buka berkas, izinkan <b>Instal dari sumber ini</b> bila diminta.</Typography></li>
                  <li><Typography variant="body2">Tekan <b>Instal</b>, lalu buka aplikasi <b>Juragan POS</b> dan login.</Typography></li>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
